import { defineStore } from 'pinia';
import { reactive, watch } from 'vue';

import type { EmailProcessingEvent, EmailProcessingStep } from '@/types/emailProcessing';
import { normalizeEmailStatus } from '@/types/emailProcessing';

import { usePusher } from './pusher';

type Subscriber = (data: EmailProcessingEvent, step: EmailProcessingStep) => void;

const STEP_PRIORITY: Record<EmailProcessingStep, number> = {
  processing: 0,
  classifying: 1,
  classified: 2,
  creating_tasks: 3,
  agent_processing: 4,
  completed: 5,
};

export const useEmailProcessingStore = defineStore('emailProcessing', () => {
  const emailProcessingState = new Map<string, { threadId: string; step: EmailProcessingStep }>();
  const processingSteps = reactive(new Map<string, EmailProcessingStep>());
  const processingTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  const subscribers = new Set<Subscriber>();

  const markThreadCompleted = (threadId: string) => {
    processingSteps.set(threadId, 'completed');
  };

  const hasActiveEmailForThread = (threadId: string) => {
    for (const state of emailProcessingState.values()) {
      if (state.threadId === threadId) return true;
    }
    return false;
  };

  const refreshThreadStep = (threadId: string) => {
    let leastAdvancedStep: EmailProcessingStep | undefined;
    let lowestPriority = Infinity;
    for (const state of emailProcessingState.values()) {
      if (state.threadId === threadId && state.step !== 'completed') {
        const priority = STEP_PRIORITY[state.step];
        if (priority < lowestPriority) {
          lowestPriority = priority;
          leastAdvancedStep = state.step;
        }
      }
    }
    if (leastAdvancedStep) {
      processingSteps.set(threadId, leastAdvancedStep);
    } else if (processingSteps.get(threadId) !== 'completed') {
      processingSteps.delete(threadId);
    }
  };

  const setProcessingStep = (emailId: string, threadId: string, step: EmailProcessingStep) => {
    // New activity on this thread — clear any stale completion sentinel.
    if (processingSteps.get(threadId) === 'completed') {
      processingSteps.delete(threadId);
    }

    emailProcessingState.set(emailId, { threadId, step });
    refreshThreadStep(threadId);

    if (processingTimeouts.has(emailId)) {
      clearTimeout(processingTimeouts.get(emailId)!);
    }
    processingTimeouts.set(
      emailId,
      setTimeout(() => {
        emailProcessingState.delete(emailId);
        processingTimeouts.delete(emailId);
        if (hasActiveEmailForThread(threadId)) {
          refreshThreadStep(threadId);
        } else {
          markThreadCompleted(threadId);
        }
      }, 30_000),
    );
  };

  const removeProcessingStep = (emailId: string, threadId: string) => {
    emailProcessingState.delete(emailId);
    if (processingTimeouts.has(emailId)) {
      clearTimeout(processingTimeouts.get(emailId)!);
      processingTimeouts.delete(emailId);
    }
    if (hasActiveEmailForThread(threadId)) {
      refreshThreadStep(threadId);
    } else {
      markThreadCompleted(threadId);
    }
  };

  const onStatusChanged = (data: EmailProcessingEvent) => {
    const step = normalizeEmailStatus(data.processing_status);
    if (!step) return;

    if (step === 'completed') {
      removeProcessingStep(data.email_id, data.thread_id);
    } else {
      setProcessingStep(data.email_id, data.thread_id, step);
    }

    for (const callback of subscribers) {
      callback(data, step);
    }
  };

  let initialized = false;

  const initialize = () => {
    if (initialized) return;
    initialized = true;

    const pusher = usePusher();

    watch(
      () => pusher.emailProcessingChannel,
      (newChannel, oldChannel) => {
        if (oldChannel) oldChannel.unbind('status_changed', onStatusChanged);
        if (newChannel) newChannel.bind('status_changed', onStatusChanged);
      },
      { immediate: true },
    );
  };

  const subscribe = (callback: Subscriber): (() => void) => {
    subscribers.add(callback);
    return () => {
      subscribers.delete(callback);
    };
  };

  const cleanup = () => {
    for (const timeout of processingTimeouts.values()) {
      clearTimeout(timeout);
    }
    processingTimeouts.clear();
    emailProcessingState.clear();
    processingSteps.clear();
    subscribers.clear();
    initialized = false;
  };

  const cleanupThread = (threadId: string) => {
    for (const [emailId, state] of emailProcessingState.entries()) {
      if (state.threadId === threadId) {
        emailProcessingState.delete(emailId);
        if (processingTimeouts.has(emailId)) {
          clearTimeout(processingTimeouts.get(emailId)!);
          processingTimeouts.delete(emailId);
        }
      }
    }
    processingSteps.delete(threadId);
  };

  return {
    processingSteps,
    emailProcessingState,
    initialize,
    setProcessingStep,
    removeProcessingStep,
    refreshThreadStep,
    subscribe,
    cleanup,
    cleanupThread,
  };
});
